import React, {FC, memo, useCallback} from "react";
import {useDispatch, useSelector} from "react-redux";
import {RootState} from "../../../store/rootReducer";
import {extraActionsMenuSelector} from "./extraActionsMenu.selector";
import {reviewActions} from "../../review.slice";
import {authActions} from "../../../auth/auth.slice";
import {confirm} from "../../../utils/confirm";
import {cardsActions} from "../../../cards/cards.slice";
import {LlmProvider, MAX_REGENERATE_INSTRUCTIONS_LENGTH, MAX_REGENERATE_TEXT_LENGTH, subcardKey} from "../../../common";
import {regenerateSeedFromMarkup} from "../../../cards/regenerate/regenerateSeed";
import {useMarkCardDialog, useMarkSubcardDialog} from "../../../cards/mark/useMarkCardDialog";
import {markInstructions} from "../../../cards/mark/markInstructions";
import {getCurrentSubcard} from "../../utils/multicard.utils";
import {ReviewState} from "../../review.interface";
import { styled } from "styled-components";
import { ThemeProps } from "../../../styles/theme";
import { navigate } from "@reach/router";
import { CardState } from "../../../common";
import { useConfirmDialog } from "../../../components/ConfirmDialogContext";

const Root = styled.div`
    position: fixed;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    z-index: 100;
`;

const UnderLayer = styled.div`
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
`;

const MenuLayer = styled.div<ThemeProps>`
    position: absolute;
    right: 10px;
    top: 43px;
    padding: 10px 20px;
    background-color: ${(props: ThemeProps) => props.theme.primaryBackground};
    border: ${(props: ThemeProps) => props.theme.primaryBorder} 1px solid;
    z-index: 101;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
`;

const MenuItem = styled.div`
    cursor: pointer;
    padding: 5px 0;
    
    &:not(:last-child) {
        border-bottom: 1px solid #eee;
        margin-bottom: 5px;
    }
`;

interface ExtraActionsMenuProps {
    children?: React.ReactNode;
}

export const ExtraActionsMenu: FC<ExtraActionsMenuProps> = memo((props) => {
    const {isMenuOpen, cardId} = useSelector(extraActionsMenuSelector);
    const hasBackup = useSelector((state: RootState) =>
        cardId !== undefined && cardId !== null ? state.cards[cardId]?.hasBackup === true : false);
    const markup = useSelector((state: RootState) =>
        cardId !== undefined && cardId !== null ? state.cards[cardId]?.markup : undefined);
    const mark = useSelector((state: RootState) =>
        cardId !== undefined && cardId !== null ? state.cards[cardId]?.mark : undefined);
    const openMarkDialog = useMarkCardDialog();
    const openMarkSubcardDialog = useMarkSubcardDialog();
    // The subcard on screen, while a subcard (not the whole card) is being reviewed.
    const currentSubcard = useSelector((state: RootState) =>
        state.review.state === ReviewState.SubcardQuestion || state.review.state === ReviewState.SubcardAnswer
            ? getCurrentSubcard(state.review)
            : undefined);
    const isCurrentSubcardMarked = currentSubcard !== undefined && mark !== undefined
        && mark.subcards.some(marked => marked.subcardKey === subcardKey(currentSubcard.question, currentSubcard.answer));
    const dispatch = useDispatch();
    const { openConfirmDialog, openPromptDialog } = useConfirmDialog();

    const clickOutsideHandler = useCallback(() => {
        dispatch(reviewActions.closeExtraActionsMenu());
    }, [dispatch]);

    const deleteHandler = useCallback(() => {
        dispatch(reviewActions.closeExtraActionsMenu());
        if (confirm('Delete?') && cardId !== undefined && cardId !== null) {
            dispatch(cardsActions.deleteCard(cardId));
            dispatch(reviewActions.loadNext());
        }
    }, [dispatch, cardId]);

    const editHandler = useCallback(() => {
        dispatch(reviewActions.closeExtraActionsMenu());
        if (cardId !== undefined && cardId !== null) {
            navigate(`/card/${cardId}`);
        }
    }, [dispatch, cardId]);

    const markAsFutureHandler = useCallback(() => {
        dispatch(reviewActions.closeExtraActionsMenu());
        if (cardId !== undefined && cardId !== null) {
            openConfirmDialog(
                'Move to Future',
                'Are you sure you want to move this card to Future state?',
                () => {
                    dispatch(cardsActions.moveToState({cardId, state: CardState.Future}));
                    dispatch(reviewActions.loadNext());
                }
            );
        }
    }, [dispatch, cardId, openConfirmDialog]);

    const markHandler = useCallback(() => {
        dispatch(reviewActions.closeExtraActionsMenu());
        if (cardId !== undefined && cardId !== null) {
            openMarkDialog(cardId, mark);
        }
    }, [dispatch, cardId, mark, openMarkDialog]);

    const markSubcardHandler = useCallback(() => {
        dispatch(reviewActions.closeExtraActionsMenu());
        if (cardId !== undefined && cardId !== null && currentSubcard !== undefined) {
            openMarkSubcardDialog(cardId, currentSubcard, mark);
        }
    }, [dispatch, cardId, currentSubcard, mark, openMarkSubcardDialog]);

    const regenerateHandler = useCallback(() => {
        dispatch(reviewActions.closeExtraActionsMenu());
        if (cardId !== undefined && cardId !== null) {
            openPromptDialog({
                title: 'Regenerate Card',
                message: 'This will replace the card content using AI. Continue?',
                field: {
                    label: 'Word or expression to generate from',
                    defaultValue: regenerateSeedFromMarkup(markup),
                    maxLength: MAX_REGENERATE_TEXT_LENGTH,
                    singleLine: true,
                },
                extraField: {
                    label: 'Additional instructions (optional)',
                    placeholder: 'For example: use only the meaning “обновлять”, not “ремонтировать”.',
                    defaultValue: markInstructions(mark),
                    maxLength: MAX_REGENERATE_INSTRUCTIONS_LENGTH,
                },
                confirmLabel: 'Regenerate',
                select: {
                    label: 'LLM provider',
                    defaultValue: 'deepseek',
                    options: [
                        {value: 'deepseek', label: 'DeepSeek'},
                        {value: 'chatgpt', label: 'ChatGPT'},
                    ],
                },
                onConfirm: ({text, extraText, selectValue}) => {
                    const trimmedText = text.trim();
                    const trimmedInstructions = extraText.trim();
                    dispatch(cardsActions.regenerateCard({
                        cardId,
                        text: trimmedText || undefined,
                        instructions: trimmedInstructions || undefined,
                        provider: selectValue as LlmProvider,
                    }));
                },
            });
        }
    }, [dispatch, cardId, mark, markup, openPromptDialog]);

    const invertHandler = useCallback(() => {
        dispatch(reviewActions.closeExtraActionsMenu());
        if (cardId !== undefined && cardId !== null) {
            openConfirmDialog(
                'Invert Card',
                'This will regenerate the card in the opposite direction using AI. Continue?',
                () => {
                    dispatch(cardsActions.invertCard(cardId));
                }
            );
        }
    }, [dispatch, cardId, openConfirmDialog]);

    const signOutHandler = useCallback(() => {
        dispatch(reviewActions.closeExtraActionsMenu());
        dispatch(authActions.signOut());
    }, [dispatch]);

    const revertHandler = useCallback(() => {
        dispatch(reviewActions.closeExtraActionsMenu());
        if (cardId !== undefined && cardId !== null) {
            openConfirmDialog(
                'Revert Card',
                'This will restore the card to its version before the last regenerate or invert. Continue?',
                () => {
                    dispatch(cardsActions.revertCard(cardId));
                }
            );
        }
    }, [dispatch, cardId, openConfirmDialog]);

    return !isMenuOpen
        ? <></>
        : (
            <Root>
                <UnderLayer onClick={clickOutsideHandler} />
                <MenuLayer>
                    <MenuItem onClick={editHandler}>
                        <span>View Card Details</span>
                    </MenuItem>
                    <MenuItem onClick={markAsFutureHandler}>
                        <span>Mark as Future</span>
                    </MenuItem>
                    {currentSubcard !== undefined && (
                        <MenuItem data-testid="review-mark-subcard" onClick={markSubcardHandler}>
                            <span>{isCurrentSubcardMarked ? 'Edit subcard mark' : 'Mark this subcard'}</span>
                        </MenuItem>
                    )}
                    <MenuItem data-testid="card-mark" onClick={markHandler}>
                        <span>{mark?.wholeCard === true ? 'Edit card mark' : 'Mark card for regeneration'}</span>
                    </MenuItem>
                    <MenuItem data-testid="card-regenerate" onClick={regenerateHandler}>
                        <span>Regenerate</span>
                    </MenuItem>
                    <MenuItem data-testid="card-invert" onClick={invertHandler}>
                        <span>Invert</span>
                    </MenuItem>
                    {hasBackup && (
                        <MenuItem data-testid="card-revert" onClick={revertHandler}>
                            <span>Undo last generation</span>
                        </MenuItem>
                    )}
                    <MenuItem onClick={deleteHandler}>
                        <span>Delete</span>
                    </MenuItem>
                    {props.children}
                    <MenuItem data-testid="sign-out" onClick={signOutHandler}>
                        <span>Sign out</span>
                    </MenuItem>
                </MenuLayer>
            </Root>
        );
});

ExtraActionsMenu.displayName = 'ExtraActionsMenu';
